import { parseConnectorsRoute } from './connectors-route';

/**
 * The canonical, cross-surface BI page vocabulary — the `pageName` every generic (src:198) event
 * carries.
 *
 * This is a DELIBERATE 1:1 MIRROR of the native apps: iOS `Route.biName`
 * (`native-apps/ios/Symphony/Navigation/AppRouter.swift`) and Android `Routes.biName`
 * (`native-apps/android/.../AppRoot.kt`). The same screen must report the same `pageName` on web, iOS
 * and Android, or no funnel can be joined across surfaces.
 *
 * It exists because web used to report its **Next.js file route** here (`index`, `settings-models`,
 * `tasks-id`) while native reported a logical screen name (`catch_up`, `agent_models`, `task_screen`).
 * `index` is a filesystem artifact, not a page; the registered field is documented as "the logical page
 * name parsed from the navigation route", so native's reading was the correct one and web moved onto it.
 *
 * Note this is NOT the legacy src:172 `page_name`, which keeps its own longer-standing vocabulary
 * (`tasks_page`, `workspace_goals`, `settings_models_page`) that existing Grafana queries are built on.
 * Where several web routes are one screen on native (the workspace's tabs), they share one page name so
 * the surfaces join; the finer web distinction stays on the src:172 event and in `componentRoute`.
 */

/** The merged Connectors catch-all — the one route whose BI name depends on its nav args. */
const CONNECTORS_PATHNAME = '/settings/connectors';

const PAGE_NAMES: Record<string, string> = {
  '/chats': 'chats',
  '/': 'catch_up',
  '/agents': 'agents',
  '/agents/[id]': 'agent_detail',
  // The account-wide AgentAction log. Deliberately NOT `activity_log`: both phones map that name to
  // `.activityLog(nil)`, which routes to their cross-workspace TASKS list, so reusing it would join two
  // different screens under one funnel. Web-only until the phones point their destination back at their
  // own log — listed in `BiPageNameParityTests.webOnlyNames` meanwhile.
  '/activity': 'agent_activity_log',
  '/agents/activity': 'activity_log',
  '/agents/personality': 'personality',
  // Alfred's settings screen. A THREE-SURFACE name: iOS `Route.alfredSettings` and Android
  // `Routes.ALFRED_SETTINGS` report the same one.
  '/alfred': 'alfred_settings',
  '/connectors': 'connectors',
  // The account-level HIPAA posture page (design §2.6). A THREE-SURFACE name: both phones' Compliance
  // screens report the same one, so the funnel joins.
  '/compliance': 'compliance',
  // The three-surface contract: iOS `Route.contacts` and Android `Routes.CONTACTS` report the same
  // two strings, so one funnel joins the screen across web, iOS and Android.
  '/contacts': 'contacts',
  '/contacts/[id]': 'contact_detail',
  '/contacts/[id]/[outreachId]': 'contact_thread',
  '/dashboard/[slug]': 'dashboard_page',
  '/files': 'files',
  '/apps': 'apps',
  '/knowledge': 'knowledge',

  '/notifications': 'notifications',
  '/onboarding/code': 'phone_code',
  '/onboarding/phone': 'phone_verify',
  '/onboarding/welcome': 'welcome',
  '/packs': 'packs',
  '/packs/[slug]': 'pack_detail',
  '/pages/[slug]': 'dashboard_page',
  '/pulses': 'pulses',
  // The Routines screen. A THREE-SURFACE name — the native apps report the same one for their own
  // Agent Routines screen, so the funnels join.
  '/routines': 'routines',
  '/settings': 'settings',
  '/welcome': 'signup_start',
  '/settings/connectors/[[...slug]]': 'connectors',
  // The two halves the merged page still reports as their own funnels, keyed by the paths that now
  // serve them (`/settings/connections` and `/settings/whatsapp` were retired with their screens in
  // Phase 5 and are aliases onto these). These rows are DECLARATIONS, not lookups: the catch-all
  // branch below computes both names from the route before this map is consulted. They live here so
  // `BI_PAGE_NAMES` states the app's whole vocabulary — which is what the cross-surface page-name
  // guard reads, and how a name that only the branch can emit stays visible to it.
  '/settings/connectors/channels': 'connections',
  '/settings/connectors/channels/whatsapp': 'whatsapp',
  '/settings/models': 'agent_models',
  '/settings/credits': 'credit_usage',
  '/settings/notifications': 'notification_settings',
  // The top-level Goals screen (mirrors '/tasks'): every live goal across every Space, narrowed to one by
  // `?workspace=<slug>`. `/workspace/[slug]/goals` below is now a redirect onto this, not a second screen.
  '/goals': 'agent_goals',
  '/tasks': 'agent_tasks',
  '/tasks/[id]': 'task_screen',
  '/wins': 'wins',
  // A workspace is ONE screen with tabs on native (`Route.workspaceHome(slug:tab:)` → `workspace`),
  // so its three web routes share that name rather than inventing three web-only ones.
  '/workspace/[slug]': 'workspace',
  '/workspace/[slug]/agents': 'workspace',
  '/workspace/[slug]/goals': 'workspace',
  '/workspace/[slug]/files': 'agent_creations',
  '/workspace/[slug]/apps': 'workspace_apps',
  '/workspace/[slug]/files/[file]': 'file_detail',
  '/workspace/[slug]/pages/[page]': 'dashboard_page',
  // Web-only surfaces — no native counterpart to align with, named in the same style.
  '/agent-oauth/authorize': 'agent_consent',
  '/connect/success': 'calendar_connect_success',
  '/connect/failure': 'calendar_connect_failure',
  // Answers every request with a redirect and never renders, but it is a route like any other.
  '/calendar-oauth-return': 'calendar_oauth_return',
  '/get-app': 'get_app',
  '/pack-accept': 'pack_accept',
  // The mobile hand-off a pack invite sends a phone to when the OS did NOT intercept the universal
  // link. It exists to get the reader BACK into the native app, so a native screen of the same name
  // would be a contradiction — see `webOnlyNames` in BiPageNameParityTests.
  '/pack-open': 'pack_open',
  // The public share card. Web-only BY CONSTRUCTION, not for want of a native screen: its reader is an
  // anonymous recipient who has no Symphony app, which is why `/s/` is pinned as unmapped in the three
  // per-link deeplink tables. Nothing on iOS or Android will ever report this name.
  '/s/[token]': 'share_card',
  // The campaign landing route. Web-only by construction: `symphony.wix.com` is deliberately not an
  // app-link host, so a promo link opens in a browser even on a phone that has the app — there is no
  // native screen for this name to join (see `webOnlyNames` in BiPageNameParityTests).
  '/promo/[code]': 'promo',
  '/packages': 'packages',
  // Web-only: a phone buys through Stripe's hosted Checkout or an in-app purchase, so there is no
  // native screen to share this name with (see `webOnlyNames` in BiPageNameParityTests).
  '/checkout': 'checkout',
  '/_error': 'error',
  // DECLARATION, not a lookup (like the connectors halves above): the MAP home is one screen that
  // replaces `/` AND `/agents` while the agents-world home is on, and all three surfaces report it
  // as `map_home` — `biPageName` computes that from the `home` option before this map is consulted.
  // The row keeps the vocabulary whole for the cross-surface guard.
  '/map-home': 'map_home',
};

/** The map home's page name — what `/` and `/agents` report on the agents-world home, on all three surfaces. */
export const MAP_HOME_PAGE_NAME = 'map_home';

/**
 * Snake_case fallback for a route that isn't registered above: drop Next.js dynamic segments
 * (`[id]`, `[...slug]`) so a template yields a stable name without leaking a concrete id, and join the
 * rest with `_`. A new page that forgets to register still reports in the right STYLE rather than as a
 * hyphenated file path — but register it (here AND on both native platforms) rather than relying on this.
 */
const fallbackPageName = (pathname: string): string => {
  const cleaned = pathname
    .replace(/^\//, '')
    .split('/')
    .filter((segment) => segment && !segment.startsWith('['))
    .join('_');
  return (
    cleaned
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'unknown'
  );
};

/**
 * The canonical BI `pageName` for a Next.js route.
 *
 * `pathname` is the route TEMPLATE (`router.pathname`) — for a catch-all that is
 * `/settings/connectors/[[...slug]]`, which carries no tab, so `query` is consulted for the one route
 * whose reported name depends on its nav args (below). Everything else is a pure path lookup.
 */
export const biPageName = (
  pathname: string,
  query?: { slug?: string | string[] },
  options?: { home?: 'today' | 'agents-world' | 'activity-map' },
): string => {
  // The ACTIVITY MAP home is the Overview screen. It reports what that screen has always reported —
  // it used to be reached as the `task-list` homepage choice — because the page name is a
  // cross-surface funnel and this change moves the screen between rollouts, not between names.
  if (options?.home === 'activity-map' && pathname === '/') return 'agent_tasks';
  // The AGENTS WORLD home is ONE screen standing in for both `/` and `/agents`, and every surface
  // reports it under one name — a funnel joined across web, iOS and Android (both phones report
  // `map_home` for their home and agents destinations on that home).
  if (options?.home === 'agents-world' && (pathname === '/' || pathname === '/agents')) return MAP_HOME_PAGE_NAME;
  // The merged Connectors page reports the HALF actually on screen, so the long-standing `connections`
  // and `whatsapp` funnels keep receiving the page-views they always did while the two halves are still
  // distinct surfaces. A 1:1 mirror of iOS `Route.biName`'s `.connectors` case and Android's
  // arg-aware branch in `AppRoot.kt`. Revisit on all three when the merged page fully lands.
  if (pathname.startsWith(CONNECTORS_PATHNAME)) {
    const raw = query?.slug;
    const segments = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const { tab, target } = parseConnectorsRoute(segments);
    if (tab === 'channels') return target === 'whatsapp' ? 'whatsapp' : 'connections';
    return 'connectors';
  }
  return PAGE_NAMES[pathname] ?? fallbackPageName(pathname);
};

/** Every canonical name this app can report — the parity test asserts these against the native sets. */
export const BI_PAGE_NAMES = PAGE_NAMES;
